import type { ThemeDefinition } from "vuetify";
import { availableThemes, type OrixaTheme } from "./index";

// Mapeamento de backgrounds claros → escuros
const darkBackgroundMap: Record<string, string> = {
	"#FFFFFF": "#121212",
	"#F5F0E6": "#1E1E1E", // ORI bege
	"#FEF2F2": "#2D1B1B", // vermelho claro
	"#ECFDF5": "#1B2D1F", // verde claro
	"#FFFBEB": "#2D2A1B", // amarelo claro
	"#F0F9FF": "#1B252D", // azul claro
	"#FAFAFA": "#181818", // cinza claro
	"#F5F5F5": "#1A1A1A", // cinza neutro (Exu)
	"#FAF5FF": "#251B2D", // roxo claro
};

// Overrides de cores para dark mode — temas cujo primary/secondary
// é escuro demais e fica invisível no fundo dark.
const darkColorOverrides: Record<string, Record<string, string>> = {
	exu: {
		primary: "#EF4444", // vermelho vira primary (preto invisível no dark)
		secondary: "#B91C1C",
		"on-primary": "#FFFFFF",
		"on-secondary": "#FFFFFF",
	},
	ogum: {
		primary: "#F87171", // vermelho mais claro
		secondary: "#B91C1C",
	},
	oxossi: {
		primary: "#34D399", // verde esmeralda mais claro
		secondary: "#38BDF8",
	},
	xango: {
		primary: "#D97706", // âmbar mais claro
		secondary: "#A8A29E",
	},
	oxum: {
		primary: "#60A5FA", // azul mais claro
		secondary: "#3B82F6",
	},
	nana: {
		primary: "#A78BFA", // roxo mais claro
		secondary: "#C084FC",
	},
};

function generateDarkVariant(lightColor: string): string {
	return darkBackgroundMap[lightColor] || lightColor;
}

function convertOrixaThemeToVuetify(
	orixaTheme: OrixaTheme,
	variant: "light" | "dark" = "light"
): ThemeDefinition {
	const colors = orixaTheme.colors;

	if (variant === "dark") {
		const overrides = darkColorOverrides[orixaTheme.name] || {};
		return {
			dark: true,
			colors: {
				...colors,
				surface: generateDarkVariant(colors.surface),
				background: generateDarkVariant(colors.background),
				"on-surface": "#FFFFFF",
				"on-background": "#FFFFFF",
				...overrides,
			},
		};
	}

	return {
		dark: false,
		colors: {
			...colors,
		},
	};
}

// Gerar todos os temas Vuetify (light e dark) a partir dos temas Orixá
export function generateVuetifyThemes(): Record<string, ThemeDefinition> {
	const themes: Record<string, ThemeDefinition> = {};

	// Adicionar tema padrão light e dark
	themes.light = {
		dark: false,
		colors: {
			primary: "#1976D2",
			secondary: "#424242",
			accent: "#82B1FF",
			error: "#E53935",
			info: "#1E88E5",
			success: "#43A047",
			warning: "#FFC107",
			surface: "#FFFFFF",
			background: "#FAFAFA",
			"on-primary": "#FFFFFF",
			"on-secondary": "#FFFFFF",
			"on-surface": "#000000",
			"on-background": "#000000",
		},
	};

	themes.dark = {
		dark: true,
		colors: {
			primary: "#2196F3",
			secondary: "#616161",
			accent: "#82B1FF",
			error: "#E53935",
			info: "#1E88E5",
			success: "#43A047",
			warning: "#FFC107",
			surface: "#121212",
			background: "#000000",
			"on-primary": "#FFFFFF",
			"on-secondary": "#FFFFFF",
			"on-surface": "#FFFFFF",
			"on-background": "#FFFFFF",
		},
	};

	// Gerar temas dos Orixás
	availableThemes.forEach((orixaTheme) => {
		themes[`${orixaTheme.name}-light`] = convertOrixaThemeToVuetify(
			orixaTheme,
			"light"
		);
		themes[`${orixaTheme.name}-dark`] = convertOrixaThemeToVuetify(
			orixaTheme,
			"dark"
		);
	});

	return themes;
}
